

import { createContext, ReactNode, useCallback, useContext, useReducer } from "react";
import { DashboardComponentTypeEnum, IDashboardComponent, IDashboardTemplateDefinition  } from "../../services/dashboard-data-service/Models";
import _ from "lodash";
import { IeLynxObject } from "../../common/models/eLynxObject";
import DashboardRunnerUtils from "../../components/dashboard/dashboard-runner/DashboardRunnerUtils";
import { Layout } from "react-grid-layout";

export type DashboardContextProviderProps = { 
    children: ReactNode; 
};

export type DashboardState = {
    originalModel: IDashboardTemplateDefinition | undefined;
    model: IDashboardTemplateDefinition | undefined;
    selectedComponent: IDashboardComponent | undefined;

};

export enum DashboardActionType {
    DashboardTemplateDefinitionChanged = 'DASHBOARD_TEMPLATE_DEFINITION_CHANGED',
    DashboardOnDrop = 'DASHBOARD_ON_DROP',
    DashboardOnRemove = 'DASHBOARD_ON_REMOVE',
    DashboardOnSelected = 'DASHBOARD_ON_SELECTED',
    DashboardOnUpdateComponent = 'DASHBOARD_ON_UPDATE_COMPONENT',
    DashboardOnLayoutChanged = 'DASHBOARD_ON_LAYOUT_CHANGED'
};

export type DashboardAction = {
    type: DashboardActionType.DashboardTemplateDefinitionChanged;
    payload: {
        templateDefinition: IDashboardTemplateDefinition | undefined;
        contextGroupID?: number;
        contextObject?: IeLynxObject;
    }
} | {
    type: DashboardActionType.DashboardOnDrop;
    payload: {
        component: IDashboardComponent
    }
} | {
    type: DashboardActionType.DashboardOnRemove;
    payload: IDashboardComponent
} | {
    type: DashboardActionType.DashboardOnSelected;
    payload: IDashboardComponent | undefined;
} | {
    type: DashboardActionType.DashboardOnUpdateComponent;
    payload: IDashboardComponent;
} | {
    type: DashboardActionType.DashboardOnLayoutChanged;
    payload: Layout[]
};


function onDrop(model: IDashboardTemplateDefinition, 
    component: IDashboardComponent) {

    if (!component) {
        console.log('no component or target');
        return;
    }

    //console.log(`IN CONTEXT received dropping column ${component.id} into component ${target.id}`);

    const updatedModel = _.cloneDeep(model);

    // "x": 1, "y": 0, "w": 3, "h": 2,    
    const ys = updatedModel.layout.map(o => o.y);
    const maxY = ys.length > 0 ? Math.max(...ys) + 1 : 0;
    //const nextY = minID >= 0 ? -1 : minID;


    const h = component.componentType === DashboardComponentTypeEnum.ObjectProperties ? 4 : 2;

    component.i = component.id;
    // these values are grid units
    component.x = 0;
    component.y = maxY;
    component.h = h;
    component.w = 3;

    updatedModel.layout.push(component);

    return updatedModel;
}


function onRemove(model: IDashboardTemplateDefinition, component: IDashboardComponent) {

    if (!component) {
        console.log('no component');
        return;
    }

    const updatedModel = _.cloneDeep(model);

    updatedModel.layout = updatedModel.layout.filter(o => o.id !== component.id);

    return updatedModel;
}    

function dashboardReducer(state: DashboardState, action: DashboardAction): DashboardState {

    switch (action.type) {
        case DashboardActionType.DashboardTemplateDefinitionChanged:

            const newModel = action.payload.templateDefinition && action.payload.contextGroupID && action.payload.contextObject ? 

                        DashboardRunnerUtils.updateModelContext(action.payload.templateDefinition, 
                            action.payload.contextGroupID, 
                            action.payload.contextObject) : 

                        undefined;

            const originalModel = !newModel ? 
                            undefined : 
                            newModel && !state.originalModel ? 
                            _.cloneDeep(newModel) : 
                            state.originalModel;

            
            return {...state,
                model: newModel,
                originalModel: originalModel,
                selectedComponent: !state.originalModel ? undefined : state.selectedComponent
            };

        case DashboardActionType.DashboardOnDrop: {
            const updatedModel = onDrop(state.model!, action.payload.component);
            return {...state,
                model: updatedModel
            };            
        }

        case DashboardActionType.DashboardOnRemove: {
                
            const deselect = action.payload.id === state.selectedComponent?.id;
            const updatedModel = onRemove(state.model!, action.payload);
            return {...state,
                model: updatedModel,
                selectedComponent: deselect ? undefined : state.selectedComponent
            };
        }

        case DashboardActionType.DashboardOnSelected: {

            return {...state,
                selectedComponent: action.payload
            };
        }

        case DashboardActionType.DashboardOnLayoutChanged: {

            const updatedModel = _.cloneDeep(state.model);


            action.payload.forEach((o) => {

                const layout = updatedModel?.layout?.find((x) => x.i === o.i);

                if (layout) {
                    layout.x = o.x;
                    layout.y = o.y;
                    layout.w = o.w;
                    layout.h = o.h;
                }

                //console.log(`Item ${o.i} moved to x: ${o.x}, y: ${o.y}, w: ${o.w}, h: ${o.h}`);

            });

            return {...state,
                model: updatedModel
            };

        }

        case DashboardActionType.DashboardOnUpdateComponent: {


            const updatedModel = _.cloneDeep(state.model!);

            const ndx = updatedModel.layout.findIndex(o => o.id === action.payload.id);

            let selectedComponent = _.cloneDeep(state.selectedComponent);

            if (ndx !== -1) {
                updatedModel.layout[ndx] = action.payload;
                if (action.payload.id === state.selectedComponent?.id) {
                    selectedComponent = action.payload;
                }
            }
            
            return {...state,
                model: updatedModel,
                selectedComponent: selectedComponent
            };

        }

        default: {
            return state;
        }
    }
}

const DashboardContext = createContext<UseDashboardStateType | undefined>(undefined);

export function DashboardContextProvider({ children }: DashboardContextProviderProps) {

    const [state, dispatchDashboardState] = useReducer(dashboardReducer, {
        originalModel: undefined,
        model: undefined,
        selectedComponent: undefined
    });


    const setModel = useCallback((templateDefinition: IDashboardTemplateDefinition | undefined, 
        contextGroupID?: number, 
        contextObject?: IeLynxObject) => {

        dispatchDashboardState({
            type: DashboardActionType.DashboardTemplateDefinitionChanged,
            payload:  {templateDefinition, contextGroupID, contextObject}
        });
    }, [dispatchDashboardState]);

    const onDrop = useCallback((component: IDashboardComponent) => {

        dispatchDashboardState({
            type: DashboardActionType.DashboardOnDrop,
            payload: { component }
        });
    }, [dispatchDashboardState]);


    const onRemove = useCallback((component: IDashboardComponent) => {

        dispatchDashboardState({
            type: DashboardActionType.DashboardOnRemove,
            payload: component
        });
    }, [dispatchDashboardState]);

    const onSelected = useCallback((component: IDashboardComponent | undefined) => {

        dispatchDashboardState({
            type: DashboardActionType.DashboardOnSelected,
            payload: component
        });
    }, [dispatchDashboardState]);    

    const updateComponent = useCallback((component: IDashboardComponent) => {

        dispatchDashboardState({
            type: DashboardActionType.DashboardOnUpdateComponent,
            payload: component
        });
    }, [dispatchDashboardState]);    


    const onLayoutChanged = useCallback((layout: Layout[]) => {
        dispatchDashboardState({
            type: DashboardActionType.DashboardOnLayoutChanged,
            payload: layout
        });

    }, [dispatchDashboardState]);


    return (
        <DashboardContext.Provider value={{ 
                state,
                setModel,
                onDrop,
                onRemove,
                onSelected,
                updateComponent,
                onLayoutChanged
        }}>
            {children}
        </DashboardContext.Provider>
    );
}

export type UseDashboardStateType = {
    state: DashboardState;
    setModel: (model: IDashboardTemplateDefinition | undefined, 
                contextGroupID?: number, 
                contextObject?: IeLynxObject,
                ) => void;
    onDrop: (component: IDashboardComponent) => void;
    onRemove: (component: IDashboardComponent) => void;
    onSelected: (component: IDashboardComponent | undefined) => void;
    updateComponent: (component: IDashboardComponent) => void;
    onLayoutChanged: (layout:  Layout[]) => void;
};

export function useDashboardContext() {
    const context = useContext<UseDashboardStateType | undefined>(DashboardContext);
    if (!context) {
        throw new Error("useDashboardContext must be used within an DashboardContextProvider");
    }
    return context;
}
